import { expandWithLifecycleSuffixes } from './lifecycle-suffixes.model';
import { SiPrefixAbbr } from './metric-units.model';

export const currencyBaseUnits = ['SEK', 'USD', 'EUR'] as const;

export type CurrencyBaseUnit = (typeof currencyBaseUnits)[number];

/**
 * @description FX rates to SEK on 14th of September 2025
 */
const fxRatesToSEK_140925 = {
  SEK_to_SEK: 1, // always one!
  USD_to_SEK: 9.3293, // https://www.investing.com/currencies/usd-sek-historical-data
  EUR_to_SEK: 10.9394, // https://www.investing.com/currencies/eur-sek-historical-data
} as const satisfies Record<`${CurrencyBaseUnit}_to_SEK`, number>;

const getRateToSEK = (currency: CurrencyBaseUnit): number =>
  fxRatesToSEK_140925[`${currency}_to_SEK`];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const acceptedCurrencyPrefixes = ['k', 'M'] as const satisfies SiPrefixAbbr[];
type AcceptedCurrencyPrefixes = (typeof acceptedCurrencyPrefixes)[number];

type CurrencyUnitCreationData = {
  name: string;
  config: (
    | { value: number }
    | {
        definition: `${number} SEK`;
      }
  ) & { aliases: string[] };
};

export const currencyAliasMap = {
  SEK: {
    name: 'SEK',
    config: {
      value: getRateToSEK('SEK'),
      aliases: [
        'sek',
        ...expandWithLifecycleSuffixes('sek'),
        ...expandWithLifecycleSuffixes('SEK'),
      ],
    },
  },
  USD: {
    name: 'USD',
    config: {
      definition: `${getRateToSEK('USD')} SEK`,
      aliases: [
        'usd',
        ...expandWithLifecycleSuffixes('usd'),
        ...expandWithLifecycleSuffixes('USD'),
      ],
    },
  },
  EUR: {
    name: 'EUR',
    config: {
      definition: `${getRateToSEK('EUR')} SEK`,
      aliases: [
        'eur',
        ...expandWithLifecycleSuffixes('eur'),
        ...expandWithLifecycleSuffixes('EUR'),
      ],
    },
  },
} as const satisfies Record<CurrencyBaseUnit, CurrencyUnitCreationData>;

/**
 * helper to get the canonical currency from any currency alias
 * @returns the canonical currency unit, or the original unit if it is not a currency alias
 */
export const getCanonicalCurrencyUnit = (() => {
  const eurAliasSet = new Set<string>(currencyAliasMap.EUR.config.aliases);
  const usdAliasSet = new Set<string>(currencyAliasMap.USD.config.aliases);
  const sekAliasSet = new Set<string>(currencyAliasMap.SEK.config.aliases);
  return (unit: string): string => {
    if (eurAliasSet.has(unit)) return 'EUR';
    if (usdAliasSet.has(unit)) return 'USD';
    if (sekAliasSet.has(unit)) return 'SEK';
    return unit;
  };
})();

type CurrencyAliases = ObjectValues<
  typeof currencyAliasMap
>['config']['aliases'][number];
type PrefixedCurrencyAliases = `${AcceptedCurrencyPrefixes}${CurrencyAliases}`;
type PrefixedCurrencyBaseUnit =
  `${AcceptedCurrencyPrefixes}${CurrencyBaseUnit}`;

export type CurrencyUnit =
  | CurrencyBaseUnit
  | PrefixedCurrencyBaseUnit
  | CurrencyAliases
  | PrefixedCurrencyAliases;
